import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { AnalysisAPI } from "../../../../../api/index.js";
import EvidenceAPI from "../../../../../api/evidence/index.js";
import { Helmet } from "react-helmet-async";
import {
  ArtifactsAPI,
  CreateTimelineEventModal,
  EventTypesAPI,
  TimelineEvent,
  TimelineEventFlyout,
  TimelineEventFormDefaultData,
  TimelineEventsTable,
  TimelineEventsTableRef,
} from "@/Features";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;
`;

export const TimelineContent: React.FC = () => {
  const { case_id: caseIDString, event_uuid } = useParams();

  if (!caseIDString) {
    throw new Error("case_id is required");
  }

  const case_id = parseInt(caseIDString);
  const navigate = useNavigate();

  const { currentUser } = usePermissions();
  const queryClient = useQueryClient();
  const timelineTableRef = useRef<TimelineEventsTableRef>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<
    Partial<TimelineEvent> | undefined
  >({
    uuid: event_uuid,
  });
  const [showFlyout, setShowFlyout] = useState(event_uuid ? true : false);

  const prefetchData = () => {
    // Prefetch queries
    queryClient.prefetchQuery({
      queryKey: ["timeline", "accounts", { case_id }],
      queryFn: () => AnalysisAPI.Accounts.get({ case_id }),
    });

    queryClient.prefetchQuery({
      queryKey: EventTypesAPI.queryKey,
      queryFn: () => EventTypesAPI.get(),
    });

    queryClient.prefetchQuery({
      queryKey: ArtifactsAPI.queryKey,
      queryFn: () => ArtifactsAPI.get(),
    });

    queryClient.prefetchQuery({
      queryKey: ["evidence", "list", { case_id }],
      queryFn: () => EvidenceAPI.getEvidence({ case_id }),
    });
  };

  const handleShowFlyout = (rowData: TimelineEvent) => {
    setSelectedEvent(rowData);
    setShowFlyout(true);
    navigate(`/cases/${case_id}/analysis/timeline/${rowData.uuid}`);
  };

  const handleCloseFlyout = () => {
    // remove event_uuid from URL
    const newUrl = `/cases/${case_id}/analysis/timeline`;
    navigate(newUrl, { replace: true });
    setSelectedEvent(undefined);
    setShowFlyout(false);
  };

  // keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) => {
      // open modal on ctrl + n
      if (ev.ctrlKey && ev.key === "n") {
        ev.preventDefault();
        setShowCreateModal(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <StyledContainer>
      <Helmet title="Case Timeline" />
      <CreateTimelineEventModal
        show={showCreateModal}
        defaultFormData={{
          created_by_id: currentUser.user_id,
          case_id,
        }}
        onClose={() => setShowCreateModal(false)}
        onCancel={() => setShowCreateModal(false)}
        onSubmit={(data: TimelineEventFormDefaultData) => {
          timelineTableRef?.current?.refresh();
          setShowCreateModal(false);
          setSelectedEvent({ uuid: data.uuid });
          setShowFlyout(true);
        }}
      />
      <TimelineEventFlyout
        open={showFlyout}
        onClose={handleCloseFlyout}
        defaultEventData={selectedEvent?.uuid ? selectedEvent : undefined}
        onEdit={() => {
          timelineTableRef?.current?.refresh();
        }}
        onDelete={() => {
          timelineTableRef?.current?.refresh();
          setSelectedEvent(undefined);
          setShowFlyout(false);
        }}
      />
      <TimelineEventsTable
        tableRef={timelineTableRef}
        query={{ case_id }}
        onAdd={() => setShowCreateModal(true)}
        onLoaded={() => prefetchData()}
        onActionButtonClick={handleShowFlyout}
      />
    </StyledContainer>
  );
};
